import { BrandAsset, BrandAssetGroup, CardBackgroundMode } from './creatives.types';

/** Prefer icons, then logos, then videos when grouping within a tab. */
const ASSET_TYPE_ORDER = ['icon', 'logo', 'video'] as const;

const VIDEO_FORMATS = new Set(['mp4', 'webm', 'mov', 'm4v']);

export const VIDEO_THUMBNAIL_URL =
  'https://shout-ugc-assets.s3.eu-west-1.amazonaws.com/other/video-play.png';

export function isVideoAsset(asset: BrandAsset): boolean {
  return asset.asset === 'video' || VIDEO_FORMATS.has(asset.format.toLowerCase());
}

export function previewUrl(asset: BrandAsset): string {
  return isVideoAsset(asset) ? VIDEO_THUMBNAIL_URL : asset.url;
}

export function defaultCardBackground(variant: string, assetType?: string): CardBackgroundMode {
  if (assetType === 'video') {
    return 'dark';
  }
  const v = variant.toLowerCase();
  if (v.includes('white')) {
    return 'dark';
  }
  if (v.includes('black') || v.includes('purple')) {
    return 'light';
  }
  return 'light';
}

export function groupAssetsByType(assets: BrandAsset[]): BrandAssetGroup[] {
  const byType = new Map<string, BrandAsset[]>();

  for (const asset of assets) {
    const type = asset.asset;
    const existing = byType.get(type);
    if (existing) {
      existing.push(asset);
    } else {
      byType.set(type, [asset]);
    }
  }

  const orderedTypes = [
    ...ASSET_TYPE_ORDER.filter((type) => byType.has(type)),
    ...[...byType.keys()].filter((type) => !ASSET_TYPE_ORDER.includes(type as (typeof ASSET_TYPE_ORDER)[number])),
  ];

  return orderedTypes.map((assetType) => ({
    assetType,
    assets: byType.get(assetType) ?? [],
  }));
}

export function formatLabel(format: string): string {
  return format.toUpperCase();
}

export function assetTypeHeading(assetType: string): string {
  if (!assetType) {
    return 'Assets';
  }
  return assetType.charAt(0).toUpperCase() + assetType.slice(1) + 's';
}

export function downloadFileName(asset: BrandAsset): string {
  const fromKey = asset.key.split('/').pop();
  return fromKey && fromKey.length > 0 ? fromKey : `asset.${asset.format}`;
}

/** Replace keeps the S3 key, so the new file must use the same extension. */
export function replacementExtensionMatches(fileName: string, format: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  const normalized = ext === 'jpeg' ? 'jpg' : ext;
  const expected = format.toLowerCase() === 'jpeg' ? 'jpg' : format.toLowerCase();
  return normalized.length > 0 && normalized === expected;
}
